/**
 * Phase 3E: Surgeon Confirmation Panel
 * 
 * UI component for explicit CPT-level surgeon confirmation.
 * 
 * Rules:
 * - Each CPT requires explicit confirmation before billable
 * - Options: CONFIRMED_PERFORMED / NOT_PERFORMED / NOT_APPLICABLE / DEFERRED
 * - Blocked if clarifications are pending
 * - Demo mode can auto-confirm all
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Ban,
  AlertTriangle,
  FileCheck,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  SurgeonConfirmationV2, 
  DocumentationStatusV2,
  BillableStatus,
  SURGEON_CONFIRMATION_LABELS,
  DOCUMENTATION_STATUS_LABELS,
} from '@/lib/phase3/types';
import { SURGEON_CONFIRMATION_OPTIONS } from '@/lib/phase3/clarificationPromptGenerator';
import { ReconciliationProgress } from '@/hooks/useClarificationWorkflow';
import { useDemoMode } from '@/contexts/DemoModeContext';

interface CptConfirmationRow {
  cptCode: string;
  cptDescription?: string;
  documentationStatus: DocumentationStatusV2;
  hasPendingClarifications: boolean;
  surgeonConfirmation: SurgeonConfirmationV2 | null;
  billableStatus: BillableStatus;
  billableReason: string;
}

interface SurgeonConfirmationPanelProps {
  cptRows: CptConfirmationRow[];
  progress: ReconciliationProgress;
  isLoading: boolean;
  onConfirm: (cptCode: string, confirmation: SurgeonConfirmationV2, reason?: string) => Promise<boolean>;
  onConfirmAllAsPerformed?: () => Promise<boolean>;
  canProceedToExport: boolean;
}

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cptCode: string;
  cptDescription?: string;
  onConfirm: (confirmation: SurgeonConfirmationV2, reason?: string) => void;
  isSubmitting: boolean;
}

function ConfirmationDialog({
  open,
  onOpenChange,
  cptCode,
  cptDescription,
  onConfirm,
  isSubmitting,
}: ConfirmationDialogProps) {
  const [selectedConfirmation, setSelectedConfirmation] = useState<SurgeonConfirmationV2 | null>(null);
  const [reason, setReason] = useState('');
  
  const handleSubmit = () => {
    if (selectedConfirmation) {
      onConfirm(selectedConfirmation, reason || undefined);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-primary" />
            Confirm CPT {cptCode}
          </DialogTitle>
          <DialogDescription>
            {cptDescription || 'Please confirm whether this procedure was performed.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Confirmation Status</Label>
            <Select
              value={selectedConfirmation || ''}
              onValueChange={(v) => setSelectedConfirmation(v as SurgeonConfirmationV2)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select confirmation status..." />
              </SelectTrigger>
              <SelectContent>
                {SURGEON_CONFIRMATION_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      {option.value === 'confirmed_performed' && <CheckCircle2 className="w-4 h-4 text-success" />}
                      {option.value === 'not_performed' && <XCircle className="w-4 h-4 text-destructive" />}
                      {option.value === 'not_applicable' && <Ban className="w-4 h-4 text-muted-foreground" />}
                      {option.value === 'deferred' && <Clock className="w-4 h-4 text-warning" />}
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedConfirmation && selectedConfirmation !== 'confirmed_performed' && (
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                placeholder="Provide additional context..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedConfirmation || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmationStatusIcon({ status }: { status: SurgeonConfirmationV2 | null }) {
  if (!status) {
    return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
  
  switch (status) {
    case 'confirmed_performed':
      return <CheckCircle2 className="w-4 h-4 text-success" />;
    case 'not_performed':
      return <XCircle className="w-4 h-4 text-destructive" />;
    case 'not_applicable':
      return <Ban className="w-4 h-4 text-muted-foreground" />;
    case 'deferred':
      return <Clock className="w-4 h-4 text-warning" />;
  }
}

function BillableStatusBadge({ status }: { status: BillableStatus }) {
  const variants = {
    billable: { className: 'bg-success/10 text-success border-success/30', label: 'Billable' },
    not_billable: { className: 'bg-destructive/10 text-destructive border-destructive/30', label: 'Not Billable' },
    review_required: { className: 'bg-warning/10 text-warning border-warning/30', label: 'Review Required' },
  };
  
  const variant = variants[status];
  
  return (
    <Badge variant="outline" className={cn('text-xs', variant.className)}>
      {variant.label}
    </Badge>
  );
}

export function SurgeonConfirmationPanel({
  cptRows,
  progress,
  isLoading,
  onConfirm,
  onConfirmAllAsPerformed,
  canProceedToExport,
}: SurgeonConfirmationPanelProps) {
  const { isDemoMode } = useDemoMode();
  const [confirmingCpt, setConfirmingCpt] = useState<string | null>(null);
  const [isConfirmingAll, setIsConfirmingAll] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCptForDialog, setSelectedCptForDialog] = useState<CptConfirmationRow | null>(null);
  
  const handleOpenConfirmDialog = (row: CptConfirmationRow) => {
    setSelectedCptForDialog(row);
    setDialogOpen(true);
  };
  
  const handleConfirm = async (confirmation: SurgeonConfirmationV2, reason?: string) => {
    if (!selectedCptForDialog) return;
    
    setConfirmingCpt(selectedCptForDialog.cptCode);
    const success = await onConfirm(selectedCptForDialog.cptCode, confirmation, reason);
    setConfirmingCpt(null);
    
    if (success) {
      setDialogOpen(false);
      setSelectedCptForDialog(null);
    }
  };
  
  const handleConfirmAllAsPerformed = async () => {
    if (!onConfirmAllAsPerformed) return;
    setIsConfirmingAll(true);
    await onConfirmAllAsPerformed();
    setIsConfirmingAll(false);
  };
  
  const pendingConfirmations = cptRows.filter(r => !r.surgeonConfirmation);
  const confirmedCount = cptRows.filter(r => r.surgeonConfirmation).length;
  const billableCount = cptRows.filter(r => r.billableStatus === 'billable').length;
  const hasPendingClarifications = cptRows.some(r => r.hasPendingClarifications);
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            Surgeon Confirmation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className={cn(
        "border-2",
        canProceedToExport ? "border-success/50" : "border-muted"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {canProceedToExport ? (
                <ShieldCheck className="w-5 h-5 text-success" />
              ) : (
                <ShieldAlert className="w-5 h-5 text-warning" />
              )}
              Surgeon Confirmation
              {isDemoMode && (
                <Badge variant="secondary" className="ml-2 text-xs">Demo</Badge>
              )}
            </CardTitle>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {confirmedCount} / {cptRows.length} confirmed
              </Badge>
              {billableCount > 0 && (
                <Badge variant="secondary" className="text-xs bg-success/10 text-success">
                  {billableCount} billable
                </Badge>
              )}
            </div>
          </div>
          
          <CardDescription>
            Each CPT requires explicit surgeon confirmation before it can be marked as billable.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Confirmation Progress</span>
              <span className="font-medium">{progress.progressPercent}%</span>
            </div>
            <Progress value={progress.progressPercent} className="h-2" />
          </div>
          
          {/* Blocking alerts */}
          {hasPendingClarifications && (
            <Alert variant="destructive" className="bg-warning/10 border-warning/30">
              <AlertTriangle className="w-4 h-4 text-warning" />
              <AlertTitle className="text-warning">Clarifications Pending</AlertTitle>
              <AlertDescription className="text-warning-foreground">
                Some CPTs have pending clarifications. Resolve all clarifications before confirming.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Demo mode batch confirm */}
          {isDemoMode && pendingConfirmations.length > 0 && onConfirmAllAsPerformed && !hasPendingClarifications && (
            <Button
              variant="outline"
              onClick={handleConfirmAllAsPerformed}
              disabled={isConfirmingAll}
              className="w-full"
            >
              {isConfirmingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Demo: Confirm All as Performed
            </Button>
          )}
          
          {/* Export ready notice */}
          {canProceedToExport && (
            <Alert className="border-success/50 bg-success/5">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <AlertTitle className="text-success">Ready for Export</AlertTitle>
              <AlertDescription>
                All confirmations complete. {billableCount} CPT(s) are marked as billable and ready for EHR export.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Confirmation table */}
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[100px]">CPT</TableHead>
                  <TableHead className="w-[200px]">Description</TableHead>
                  <TableHead className="w-[120px]">Doc Status</TableHead>
                  <TableHead className="w-[150px]">Confirmation</TableHead>
                  <TableHead className="w-[120px]">Billable</TableHead>
                  <TableHead className="w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cptRows.map(row => (
                  <TableRow 
                    key={row.cptCode}
                    className={cn(
                      row.billableStatus === 'billable' && "bg-success/5",
                      row.surgeonConfirmation === 'not_performed' && "bg-destructive/5"
                    )}
                  >
                    <TableCell className="font-mono font-medium">
                      {row.cptCode}
                    </TableCell>
                    
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {row.cptDescription || '—'}
                    </TableCell>
                    
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-xs",
                          row.documentationStatus === 'sufficient' && "bg-success/10 text-success",
                          row.documentationStatus === 'insufficient' && "bg-destructive/10 text-destructive",
                          row.documentationStatus === 'ambiguous' && "bg-warning/10 text-warning"
                        )}
                      >
                        {DOCUMENTATION_STATUS_LABELS[row.documentationStatus]}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ConfirmationStatusIcon status={row.surgeonConfirmation} />
                        <span className="text-sm">
                          {row.surgeonConfirmation 
                            ? SURGEON_CONFIRMATION_LABELS[row.surgeonConfirmation]
                            : 'Pending'}
                        </span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <BillableStatusBadge status={row.billableStatus} />
                    </TableCell>
                    
                    <TableCell>
                      {!row.surgeonConfirmation ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenConfirmDialog(row)}
                          disabled={row.hasPendingClarifications || confirmingCpt === row.cptCode}
                          className="h-8"
                        >
                          {confirmingCpt === row.cptCode ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : row.hasPendingClarifications ? (
                            <HelpCircle className="w-3 h-3" />
                          ) : (
                            <CheckCircle2 className="w-3 h-3" />
                          )}
                          <span className="ml-1">Confirm</span>
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Confirmed</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Billable rules reminder */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
            <p className="font-medium text-foreground mb-1">Billable Criteria:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
              <li>CPT must be authorized</li>
              <li>Documentation status must be "Sufficient"</li>
              <li>Surgeon confirmation must be "Confirmed Performed"</li>
              <li>All clarifications must be resolved</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      
      {/* Confirmation Dialog */}
      {selectedCptForDialog && (
        <ConfirmationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          cptCode={selectedCptForDialog.cptCode}
          cptDescription={selectedCptForDialog.cptDescription}
          onConfirm={handleConfirm}
          isSubmitting={!!confirmingCpt}
        />
      )}
    </>
  );
}
